import { Card } from '@/components/ui/Card';
import { usePerformanceSummary } from '@/hooks/queries/useAnalytics';
import { formatCurrency } from '@/lib/formatters';
import { cn } from '@/lib/utils';

export function PerformanceMetrics() {
  const { data: summary, isLoading } = usePerformanceSummary();

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-2" />
            <div className="h-8 bg-gray-200 rounded w-3/4" />
          </Card>
        ))}
      </div>
    );
  }

  if (!summary) return null;

  const winRate = summary.winRate ?? 0;
  const profitFactor = summary.profitFactor ?? 0;
  const totalRealizedPnl = summary.totalRealizedPnl ?? 0;
  const openPositionsCount = summary.openPositionsCount ?? 0;

  const metrics = [
    {
      label: 'Total Realized P&L',
      value: formatCurrency(totalRealizedPnl),
      color: totalRealizedPnl >= 0 ? 'text-green-600' : 'text-red-600',
      bg: totalRealizedPnl >= 0 ? 'bg-green-50' : 'bg-red-50',
    },
    {
      label: 'Win Rate',
      value: `${winRate.toFixed(1)}%`,
      color: winRate >= 50 ? 'text-green-600' : 'text-yellow-600',
      bg: winRate >= 50 ? 'bg-green-50' : 'bg-yellow-50',
    },
    {
      label: 'Profit Factor',
      value: profitFactor === Infinity || profitFactor === 0 ? '---' : profitFactor.toFixed(2),
      color: profitFactor >= 1.5 ? 'text-green-600' : profitFactor >= 1 ? 'text-yellow-600' : 'text-red-600',
      bg: 'bg-gray-50',
    },
    {
      label: 'Average Win',
      value: formatCurrency(summary.averageWin),
      color: 'text-green-600',
      bg: 'bg-green-50',
    },
    {
      label: 'Average Loss',
      value: formatCurrency(summary.averageLoss),
      color: 'text-red-600',
      bg: 'bg-red-50',
    },
    {
      label: 'Premium Collected',
      value: formatCurrency(summary.totalPremiumCollected),
      color: 'text-green-600',
      bg: 'bg-gray-50',
    },
    {
      label: 'Premium Paid',
      value: formatCurrency(summary.totalPremiumPaid),
      color: 'text-gray-900',
      bg: 'bg-gray-50',
    },
    {
      label: 'Open Positions',
      value: openPositionsCount.toString(),
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {metrics.map((metric) => (
        <Card key={metric.label} className={metric.bg}>
          <p className="text-sm text-gray-500">{metric.label}</p>
          <p className={cn('text-xl font-bold mt-1', metric.color)}>{metric.value}</p>
        </Card>
      ))}
    </div>
  );
}
